import { RefObject, useCallback } from "react";
import type { FormShallowStoreHook } from "../../store";

export interface UseTouchedFieldsArgs {
  validateFieldRef: RefObject<(args: { fieldId: string }) => Promise<string | null>>;
  useFormShallowStore: FormShallowStoreHook;
}

export const useTouchedFields = ({ validateFieldRef, useFormShallowStore }: UseTouchedFieldsArgs) => {
  const {
    touchedFields,
    setTouchedFields,
    repeatInputs,
    setRepeatInputs,
    repeatTouchedFields,
    setRepeatTouchedFields,
  } = useFormShallowStore(
    "touchedFields",
    "setTouchedFields",
    "repeatInputs",
    "setRepeatInputs",
    "repeatTouchedFields",
    "setRepeatTouchedFields"
  );

  const setRepeatValue = useCallback(
    (fieldId: string, value: string) => {
      setRepeatInputs({ ...repeatInputs, [fieldId]: value });
    },
    [repeatInputs, setRepeatInputs]
  );

  const handleFieldBlur = useCallback(
    (fieldId: string) => {
      if (!touchedFields.has(fieldId)) setTouchedFields(new Set([...touchedFields, fieldId]));
      validateFieldRef.current?.({ fieldId });
    },
    [validateFieldRef, touchedFields, setTouchedFields]
  );

  const markFieldTouched = useCallback(
    (fieldId: string) => {
      if (touchedFields.has(fieldId)) return;
      setTouchedFields(new Set([...touchedFields, fieldId]));
    },
    [touchedFields, setTouchedFields]
  );

  const handleRepeatBlur = useCallback(
    (fieldId: string) => {
      if (!repeatTouchedFields.has(fieldId)) setRepeatTouchedFields(new Set([...repeatTouchedFields, fieldId]));
      if (!touchedFields.has(fieldId)) setTouchedFields(new Set([...touchedFields, fieldId]));
      validateFieldRef.current?.({ fieldId });
    },
    [validateFieldRef, touchedFields, setTouchedFields, repeatTouchedFields, setRepeatTouchedFields]
  );

  return {
    touchedFields,
    setTouchedFields,
    repeatInputs,
    setRepeatValue,
    repeatTouchedFields,
    handleFieldBlur,
    handleRepeatBlur,
    markFieldTouched,
  };
};
